import { NextAction, NextActionInput, NextActionRuleId } from "@/types/dashboard/genie-console";

/**
 * The "do this next" ranking (HG-1231).
 *
 * Pure by design: no React, no React Query, no network. The console hook
 * assembles a flat `NextActionInput` and this decides the single highest-value
 * move. Rules are evaluated strictly in order and the FIRST match wins — the
 * dashboard renders exactly one sentence and one button, never a list.
 *
 * Ordering (locked by `nextAction.test.ts`):
 *   1. no genie           2. no knowledge        3. unanswered questions
 *   4. unprinted pack     5. no scans            6. stale documents
 *   7. everything healthy
 */

/** A document untouched for this long is treated as stale. */
export const STALE_DOCUMENT_DAYS = 90;

interface RuleDefinition {
  id: NextActionRuleId;
  matches: (input: NextActionInput) => boolean;
  build: (input: NextActionInput) => Omit<NextAction, "ruleId">;
}

const genieLabel = (input: NextActionInput) => input.genieName || "your genie";

const knowledgeRoute = (input: NextActionInput) =>
  input.genieId ? `/genie-hub/knowledge-base/${input.genieId}` : "/genie-hub/knowledge-base";

// The zero-question path: creates (or opens) this genie's pack with no
// create-pack form in between. Falls back to the list when no single genie
// is selected — the list is where that choice gets made instead.
const packEntryRoute = (input: NextActionInput) =>
  input.genieId ? `/genie-hub/activation/genie/${input.genieId}` : "/genie-hub/activation";

const RULES: RuleDefinition[] = [
  // 1 — nothing exists yet.
  {
    id: "create-genie",
    matches: (input) => input.genieCount === 0,
    build: () => ({
      tone: "action",
      sentence: "You don't have a Genie yet — build one and it can start answering today.",
      buttonLabel: "Create your first Genie",
      to: "/welcome/business",
    }),
  },
  // 2 — a genie with nothing to say.
  {
    id: "add-knowledge",
    matches: (input) => input.genieCount > 0 && input.documentCount === 0,
    build: (input) => ({
      tone: "action",
      sentence: `${genieLabel(input)} has no knowledge yet, so every answer is a guess.`,
      buttonLabel: "Add knowledge",
      to: knowledgeRoute(input),
    }),
  },
  // 3 — real customers are waiting on the owner.
  {
    id: "review-conversations",
    matches: (input) => input.unreviewedConversationCount > 0,
    build: (input) => ({
      tone: "action",
      sentence:
        input.unreviewedConversationCount === 1
          ? "1 conversation is waiting for you to read it."
          : `${input.unreviewedConversationCount} conversations are waiting for you to read them.`,
      buttonLabel: "Open the inbox",
      to: "/genie-hub/inbox",
    }),
  },
  // 4 — the pack exists on screen but never made it onto anything physical.
  //     Skipped whenever the print state is unknown (see PrintOrderState).
  {
    id: "print-pack",
    matches: (input) =>
      input.packCount > 0 &&
      (input.printOrderStatus === "draft" || input.printOrderStatus === "none"),
    build: (input) => ({
      tone: "action",
      sentence: "Your activation pack is ready but nothing has been sent to print yet.",
      buttonLabel: "Send it to print",
      to: input.firstPackId
        ? `/genie-hub/activation/${input.firstPackId}`
        : "/genie-hub/activation",
    }),
  },
  // 5 — printed and placed, but nobody has scanned it.
  {
    id: "share-genie",
    matches: (input) => input.genieCount > 0 && input.scanCount === 0,
    build: (input) => ({
      tone: "action",
      sentence: `Nobody has scanned their way to ${genieLabel(input)} yet — put the code where customers are.`,
      buttonLabel: "Share your Genie",
      to: input.packCount > 0 ? "/genie-hub/activation" : packEntryRoute(input),
    }),
  },
  // 6 — the knowledge is drifting out of date.
  {
    id: "refresh-knowledge",
    matches: (input) => input.staleDocumentCount > 0,
    build: (input) => ({
      tone: "action",
      sentence:
        input.staleDocumentCount === 1
          ? `1 document hasn't been touched in over ${STALE_DOCUMENT_DAYS} days.`
          : `${input.staleDocumentCount} documents haven't been touched in over ${STALE_DOCUMENT_DAYS} days.`,
      buttonLabel: "Refresh knowledge",
      to: knowledgeRoute(input),
    }),
  },
  // 7 — healthy. Same weight as every other state, never a greyed-out footnote.
  {
    id: "healthy",
    matches: () => true,
    build: (input) => ({
      tone: "healthy",
      sentence: `${genieLabel(input)} is fed, findable, and answering — go see what customers asked.`,
      buttonLabel: "See what's happening",
      to: "/genie-hub/insights",
    }),
  },
];

/** Rule identifiers in evaluation order — exported so the test can't drift. */
export const NEXT_ACTION_RULE_ORDER: NextActionRuleId[] = RULES.map((rule) => rule.id);

/**
 * Resolve the single highest-value next action.
 * Returns `null` while the console has not settled, so the dashboard shows a
 * placeholder rather than flashing the wrong advice at a half-loaded account.
 */
export const resolveNextAction = (input: NextActionInput): NextAction | null => {
  if (!input.isReady) return null;

  const rule = RULES.find((candidate) => candidate.matches(input));
  if (!rule) return null;

  return { ruleId: rule.id, ...rule.build(input) };
};
